import { useContext, useMemo } from "react";
import { UserContext } from "contexts/UserContext/UserContext";
import { IEventReportDetails } from "entities/EventReports/EventReportsEntityInterfaces";
import { DetailsReportEditionContext } from "contexts/DetailsReportEditionContext/DetailsReportEditionContext";
import { FAILURE_TYPES_CODE_EXCEPTIONS } from "constants/constants";
import { useTranslation } from "react-i18next";
import { AreaField } from "../SegmentFields";

/**
 * Inline-edit adapter: binds the controlled AreaField to the
 * DetailsReportEditionContext for the table's row edit.
 */
export const AreasDropdown = ({ row }: { row: IEventReportDetails }) => {
    const { user_id } = useContext(UserContext);
    const { edit, setEdit } = useContext(DetailsReportEditionContext);
    const { t } = useTranslation();

    const existingEdition = useMemo(() => edit.findIndex((event) => event.id === row.event_report_id), [edit, row]);
    const selectedFailureTypeIdIsNotSelected = useMemo(
        () =>
            row.failure_type.failure_type_id === edit[existingEdition]?.changes?.failure_type_id &&
            row.failure_type.code === FAILURE_TYPES_CODE_EXCEPTIONS.NOT_SELECTED,
        [row, existingEdition, edit],
    );
    const selectedFailureTypeId = useMemo(
        () => (selectedFailureTypeIdIsNotSelected ? undefined : edit[existingEdition]?.changes?.failure_type_id),
        [existingEdition, edit, selectedFailureTypeIdIsNotSelected],
    );
    const selectedAreaId = useMemo(() => edit[existingEdition]?.changes?.area_id, [edit, existingEdition]);

    const handleAreaUpdate = (id: number) => {
        if (existingEdition === -1) return;
        setEdit((prevEdit) => {
            const newEdit = [...prevEdit];
            newEdit[existingEdition] = {
                ...newEdit[existingEdition],
                changes: {
                    ...newEdit[existingEdition]?.changes,
                    area_id: id,
                    functional_location_id: undefined,
                    failure_code_id: undefined,
                    ...(user_id && { edited_by_user_id: user_id }),
                },
            };
            return newEdit;
        });
    };

    return selectedFailureTypeId && edit[existingEdition]?.location_specific ? (
        <AreaField
            machineCenterId={row.machine_center.machine_center_id}
            failureTypeId={selectedFailureTypeId}
            value={selectedAreaId}
            onChange={handleAreaUpdate}
        />
    ) : (
        <span>{t("locations.mill_dashboards.details.not_applicable")}</span>
    );
};
